/**
 * Currency helpers: guess a user's currency from their locale and show a
 * nicely rounded "about $5" equivalent.
 *
 * Supported currencies follow the PayPal list on https://support.cheqdin.com/support/solutions/articles/48001177560-supported-currencies-paypal (page last modified 31 Mar 2023; PayPal's own list is the source of truth:
 *
 * https://developer.paypal.com/docs/reports/reference/paypal-supported-currencies/).
 *
 * Anything outside this list falls back to USD.
 */

import {SUGGESTED_DONATION} from "./donate";

/**
 * Region -> currency
 */
const EUR_REGIONS = [
    // Eurozone (Bulgaria joined 1 Jan 2026)
    "AT", "BE", "BG", "HR", "CY", "EE", "FI", "FR", "DE", "GR", "IE",
    "IT", "LV", "LT", "LU", "MT", "NL", "PT", "SK", "SI", "ES",
    // Non-members that use the euro
    "AD", "MC", "SM", "VA", "ME", "XK",
    // French / Finnish territories on the euro
    "GP", "MQ", "RE", "GF", "YT", "PM", "BL", "MF", "AX",
];
const USD_REGIONS = ["US", "PR", "GU", "VI", "AS", "MP", "EC", "SV", "PA", "TL"];
const OTHER_REGION_CURRENCY: Record<string, string> = {
    // Europe, non-euro
    GB: "GBP", CH: "CHF", LI: "CHF", SE: "SEK", NO: "NOK", DK: "DKK",
    PL: "PLN", CZ: "CZK", HU: "HUF", RU: "RUB",
    // Americas
    CA: "CAD", MX: "MXN",
    // Asia-Pacific
    JP: "JPY", HK: "HKD", TW: "TWD", SG: "SGD", TH: "THB", PH: "PHP",
    AU: "AUD", NZ: "NZD",
    // Middle East
    IL: "ILS",

    // --- Restricted: PayPal supports these only for in-country accounts. ---
    // Kept for reference, but disabled so they fall back to USD.
    // BR: "BRL", // Brazil: payment currency / balance for in-country PayPal accounts only
    // IN: "INR", // India: in-country PayPal India accounts only
    // CN: "CNY", // China: in-country PayPal accounts only
    // MY: "MYR", // Malaysia: in-country PayPal accounts only
};

export const REGION_CURRENCY: Record<string, string> = {
    ...OTHER_REGION_CURRENCY,
    ...Object.fromEntries(USD_REGIONS.map((r) => [r, "USD"])),
    ...Object.fromEntries(EUR_REGIONS.map((r) => [r, "EUR"])),
};

// Approximate rates (only used to pick a nice number, so staleness is fine)

/** Rough units of currency per 1 USD. Keys double as the supported-currency list. */
const APPROX_PER_USD: Record<string, number> = {
    USD: 1, EUR: 0.9, GBP: 0.78, CHF: 0.85, SEK: 10, NOK: 10.5, DKK: 6.7, PLN: 3.8, CZK: 22, HUF: 350, RUB: 90, CAD: 1.37, MXN: 18, JPY: 150, HKD: 7.8, TWD: 32, SGD: 1.35, THB: 35, PHP: 57, AUD: 1.5, NZD: 1.65, ILS: 3.7,
    // Restricted: in-country PayPal accounts only (see note above).
    //
    // BRL: 5.3,  // Brazil
    // INR: 85,   // India
    // CNY: 7.2,  // China
    // MYR: 4.5,  // Malaysia
};

export const SUPPORTED_CURRENCIES: ReadonlySet<string> = new Set(Object.keys(APPROX_PER_USD));

/** Currencies that do not support decimals; PayPal errors on a fractional amount. */
const NO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(["HUF", "JPY", "TWD"]);

// Currency guessing
export function defaultLocale(): string | undefined { return typeof navigator !== "undefined" ? navigator.language : undefined; }

/** Best-guess supported currency for a locale. Falls back to "USD". */
export function guessCurrency(locale: string | undefined = defaultLocale()): string {
    if (!locale) return "USD";
    try {
        const region = new Intl.Locale(locale).maximize().region;
        return (region && REGION_CURRENCY[region]) || "USD";
    } catch {
        return "USD";
    }
}
export function guessCurrencyOrUndefined(locale: string | undefined = defaultLocale()): string | undefined {
    if (!locale) return undefined;
    try {
        const region = new Intl.Locale(locale).maximize().region;
        return (region && REGION_CURRENCY[region]) || undefined;
    } catch {
        return undefined;
    }
}
// Formatting + rounding
/** Nearest "nice" number (m x 10^n for m in `mantissas`) to `raw`, compared on a log scale. */
function snapNice(raw: number, mantissas: number[] = [1, 2, 5]): number {
    const exp = Math.floor(Math.log10(raw));
    let best = raw;
    let bestErr = Infinity;
    for (let e = exp - 1; e <= exp + 1; e++) {
        for (const m of mantissas) {
            const candidate = m * 10 ** e;
            const err = Math.abs(Math.log(candidate / raw));
            if (err < bestErr) {
                bestErr = err;
                best = candidate;
            }
        }
    }
    return best;
}
const formatters = new Map<string, Intl.NumberFormat>();
export function getFormatter(locale: string | undefined, currency: string, fractionDigits: number) {
    const key = `${locale ?? ""}|${currency}|${fractionDigits}`;
    let fmt = formatters.get(key);
    if (!fmt) {
        fmt = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        });
        formatters.set(key, fmt);
    }
    return fmt;
}
export interface NiceAmount {
    /** Formatted for display, e.g. "$5", "¥1,000", "5 €". */
    text: string;
    /** Isolated ISO 4217 code: "USD", "EUR", ... */
    currency: string;
    /** The rounded numeric amount, e.g. 1000. */
    amount: number;
}

function convertTo(requestedCurrency: string, usd: number, locale: string | undefined): NiceAmount {
    const currency = SUPPORTED_CURRENCIES.has(requestedCurrency) ? requestedCurrency : "USD";
    const perUsd: number = APPROX_PER_USD[currency];

    let amount: number = snapNice(usd * perUsd);
    if (NO_DECIMAL_CURRENCIES.has(currency)) amount = Math.round(amount);

    const fractionDigits = Number.isInteger(amount) ? 0 : 2;
    return { text: getFormatter(locale, currency, fractionDigits).format(amount), currency, amount };
}

/**
 * Roughly what $5 is worth in the user's currency, snapped to a nicely
 * rounded number (1 / 2 / 5 / 10 / 20 / 50 / 100 / ...). Not rate-accurate on purpose.
 *
 * - `currency` overrides the locale guess (e.g. a saved user preference).
 * - Unsupported or restricted currencies (e.g. BRL) fall back to USD.
 */
export function usdEquivalent(usd: number = SUGGESTED_DONATION, opts: { locale?: string; currency?: string } = {}): NiceAmount {
    const locale: string | undefined = opts.locale ?? defaultLocale();
    const requestedCurrency: string = opts.currency ?? guessCurrency(locale);
    return convertTo(requestedCurrency, usd, locale);
}

// Examples:
// usd5Equivalent({ locale: "en-US" }); // { text: "$5",     currency: "USD", amount: 5 }
// usd5Equivalent({ locale: "de-DE" }); // { text: "5 €",    currency: "EUR", amount: 5 }
// usd5Equivalent({ locale: "ja-JP" }); // { text: "¥1,000", currency: "JPY", amount: 1000 }
// usd5Equivalent({ locale: "pt-BR" }); // { text: "$5",     currency: "USD", amount: 5 }  (BRL restricted)